import type { ScreenComposerLayout } from '../../../components/ScreenComposer/composer.types';

export const MANAGE_SCREENS_PATH = '/beamonscreen/manage-screens';

export type ScreenCommandType = 'reboot' | 'clear-cache' | 'clear-browser-cache' | 'restart-browser' | 'factory-reset' | 'upgrade';
export type ScreenCommandStatus = 'pending' | 'delivered' | 'done' | 'failed';
export type ScreenSyncState = 'idle' | 'caching' | 'cached' | 'error';
export type ScreenUpgradeState = 'none' | 'downloading' | 'installing' | 'rebooting' | 'failed';
export type ScreenOrientation = 'landscape' | 'portrait' | 'landscape-flipped' | 'portrait-flipped';
export type ScreenLogLevel = 'info' | 'debug';

export interface ScreenDeviceStatus {
    clientVersion: string;
    os: string;
    arch: string;
    hostname: string;
    ip: string;
    uptimeSec: number;
    diskFreeBytes: number;
    /** Present when the screen client reports the size of the data disk. */
    diskTotalBytes?: number;
    cpuPercent?: number;
    memUsedBytes?: number;
    memTotalBytes?: number;
    resolution: string;
    syncState: ScreenSyncState;
    progress: { filesDone: number; filesTotal: number; bytesDone: number; bytesTotal: number; reelsDone?: number; reelsTotal?: number };
    cachedPlaylistId: string;
    cachedRevision: number;
    cachedManifestHash: string;
    reelsCount: number;
    browserState: string;
    browserCacheClearedAt: string | null;
    upgradeState: ScreenUpgradeState;
    logShipping: { lastShippedAt: string | null; backlogLines: number; lastError: string };
    lastError: string;
    drivers?: { lastRunAt: string | null; installed: string[]; missing: string[] };
    nics?: Array<{ name: string; mac: string; carrier: boolean; address: string }>;
    audioDevices?: Array<{ id: string; name: string }>;
}

export interface ScreenResourceSample {
    at: string;
    cpuPercent: number;
    memUsedBytes: number;
    memTotalBytes: number;
    diskUsedBytes: number;
    diskTotalBytes: number;
    netBytesPerSec?: number;
}

export interface ScreenNetworkSpeed {
    bytesPerSec: number;
    at: string | null;
    /** When the screen first reported a speed. Later samples keep this and refresh `at`. */
    firstAt?: string | null;
}

/** `BOS-Screen` as returned by `/bos/screens`. */
export interface Screen {
    _id: string;
    companyId: string;
    machineId: string;
    name: string;
    location: string;
    playlistId: string | null;
    playlistName: string;
    playlistRevision: number;
    targetClientVersion: string;
    status: 'active' | 'factory-reset-pending';
    settings: { orientation: ScreenOrientation; volume: number; logLevel: ScreenLogLevel; audioDevice?: string };
    registeredAt: string;
    lastSeenAt: string | null;
    /** When the portal last changed a setting the screen applies on its next poll. */
    configChangedAt?: string | null;
    deviceStatus: ScreenDeviceStatus;
    resourceSamples?: ScreenResourceSample[];
    networkSpeed?: ScreenNetworkSpeed;
    nowPlaying: { reelId: string; version: number; name: string; playlistId: string; startedAt: string | null };
    online: boolean;
    upgradePending: boolean;
    /** IANA timezone the device uses (from its location; UTC without one). */
    timezone?: string;
    /** Public On Demand Player URL encoded in the widget QR code. */
    onDemandUrl?: string;
    composer?: ScreenComposerLayout;
    createdAt: string;
    updatedAt: string;
}

export interface WeatherPreview {
    available: boolean;
    reason?: string;
    location?: string;
    tempC?: number;
    icon?: string;
    label?: string;
}

export interface NewsFeedPreview {
    title: string;
    fields: string[];
    items: Array<Record<string, string>>;
    fetchedAt: string;
}

export interface ScreensResponse {
    onlineWindowSec: number;
    pollSec: number;
    serverTime: string;
    screens: Screen[];
}

export interface ScreenCommand {
    _id: string;
    screenId: string;
    type: ScreenCommandType;
    payload: { targetVersion?: string };
    status: ScreenCommandStatus;
    createdBy: string;
    createdAt: string;
    deliveredAt: string | null;
    completedAt: string | null;
    result: string;
}

export interface ScreenClientArtifact {
    arch: string;
    path: string;
    url: string;
    sha256: string;
    sizeBytes: number;
}

export interface ScreenClientRelease {
    version: string;
    channel: 'releases' | 'dev';
    notes: string;
    gitCommit: string;
    gitBranch: string;
    builtAt: string;
    artifacts: ScreenClientArtifact[];
    installers?: ScreenClientArtifact[];
}

export interface ScreenSettings {
    companyId: string;
    secretKey: string;
    regeneratedAt: string | null;
    apiBaseUrl: string;
    latestRelease: ScreenClientRelease | null;
}

export interface PlaybackLogRow {
    _id: string;
    reelId: string;
    version: number;
    reelName: string;
    playlistId: string;
    startedAt: string;
    endedAt: string | null;
    durationMs: number;
    endReason: string;
}

export const COMMAND_LABEL: Record<ScreenCommandType, string> = {
    reboot: 'Reboot',
    'clear-cache': 'Clear cache',
    'clear-browser-cache': 'Clear browser cache',
    'restart-browser': 'Restart browser',
    'factory-reset': 'Factory reset',
    upgrade: 'Upgrade',
};

/** 0–100 from reels finished. A 10-reel playlist moves 10% each time one reel is fully on disk. */
export const cachePercent = (progress?: { reelsDone?: number; reelsTotal?: number; filesDone: number; filesTotal: number; bytesDone: number; bytesTotal: number } | null): number => {
    if (!progress) return 0;
    const reelsTotal = progress.reelsTotal ?? 0;
    if (reelsTotal > 0) return Math.max(0, Math.min(100, Math.round(((progress.reelsDone ?? 0) / reelsTotal) * 100)));
    if (progress.filesTotal > 0) return Math.max(0, Math.min(100, Math.round((progress.filesDone / progress.filesTotal) * 100)));
    if (progress.bytesTotal > 0) return Math.max(0, Math.min(100, Math.round((progress.bytesDone / progress.bytesTotal) * 100)));
    return 0;
};

export const formatBytes = (bytes: number): string => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
    return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

export const formatAgo = (iso: string | null | undefined, now = Date.now()): string => {
    if (!iso) return 'never';
    const sec = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
};
